<template>
  <div>
    <div v-show="pictures.length > 1" :class="$style.wrapper">
      <div v-if="isShow" :class="$style.photoPager">
        <p>{{ `${current + 1} / ${pictures.length}` }}</p>
      </div>
      <Swiper :modules="[Navigation, Zoom]" :navigation="true" :zoom="true" :loop="true" :initial-slide="currentIndex" :class="$style.swiper" @swiper="onSwiper" @slide-change="onSlideChange">
        <SwiperSlide v-for="(picture, index) in pictures" :key="index" :data-swiper-slide-index="index">
          <div :class="$style.dialog">
            <div class="swiper-zoom-container" :class="$style.imageWrap">
              <img :src="dynamicImageUrl(picture.path, { width: Math.min(picture.width, 1280) })" :alt="picture.alt" :width="picture.width" :height="picture.height" :loading="current === index ? 'eager' : 'lazy'" />
            </div>
          </div>
        </SwiperSlide>
      </Swiper>
    </div>
    <div v-if="pictures.length === 1" :class="$style.wrapper">
      <div :class="$style.dialog">
        <div class="swiper-zoom-container" :class="$style.imageWrap">
          <img :src="dynamicImageUrl(pictures[0].path, { width: Math.min(pictures[0].width, 1280) })" :alt="pictures[0].alt" :width="pictures[0].width" :height="pictures[0].height" />
        </div>
      </div>
    </div>
    <transition name="fade">
      <div v-if="comment" :class="$style.dialogComment">{{ comment }}</div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import type { Swiper as SwiperInstance } from 'swiper';
import { Navigation, Zoom } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { ref, computed } from 'vue';
import { dynamicImageUrl } from '@/js/lib/helper/url';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/zoom';

const props = withDefaults(
  defineProps<{
    pictures?: {
      path: string;
      alt: string;
      width: number;
      height: number;
      comment: string;
    }[];
    currentIndex?: number;
    isShowModal?: boolean;
  }>(),
  {
    pictures: () => [],
    currentIndex: 0,
  },
);
const emit = defineEmits<{
  (e: 'change', index: number): void;
}>();
const swiperInstance = ref<SwiperInstance>();
const current = ref(props.currentIndex);
const isShow = computed(() => props.isShowModal);
const comment = computed(() => props.pictures[current.value]?.comment);

const onSwiper = (swiper: SwiperInstance) => {
  swiperInstance.value = swiper;
};
const onSlideChange = (swiper: SwiperInstance) => {
  current.value = swiper.realIndex;
  emit('change', swiper.realIndex);
};
</script>

<style module lang="scss">
.wrapper {
  aspect-ratio: 1 / 1;
  max-height: 100vh;
  max-height: 100dvh;
  max-width: 100vw;
  width: 100%;
}

.photoPager {
  left: 0;
  padding: 20px;
  pointer-events: none;
  position: fixed;
  text-align: center;
  top: 0;
  width: 100%;
  z-index: 100;

  @media (--sp) {
    padding: 12px;
  }

  p {
    background-color: var(--color-black-alpha40);
    border-radius: 20px;
    color: var(--color-white);
    display: inline-block;
    padding: 4px 10px;
  }
}

.dialog {
  background: var(--color-gray100) url('@/svgs/vue/loader.svg') no-repeat center center;

  .imageWrap {
    aspect-ratio: 1 / 1;

    img {
      height: 100%;
      object-fit: contain;
      width: 100%;
    }
  }
}

.dialogComment {
  background-color: var(--color-black-alpha40);
  bottom: 0;
  color: var(--color-white);
  left: 50%;
  max-width: 100vh;
  padding: 20px;
  pointer-events: none;
  position: fixed;
  transform: translateX(-50%);
  white-space: pre-wrap;
  width: 100%;
  z-index: 10;

  @media (--sp) {
    font-size: 12px;
    left: 0;
    max-width: initial;
    padding: 10px;
    transform: none;
  }

  a {
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }
}
</style>
